import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  ArrowRight,
  AtSign,
  Check,
  Eye,
  EyeOff,
  KeyRound,
  Mail,
  ShieldCheck,
  Terminal,
} from "lucide-react";
import type { TwoFactorChallenge } from "@ide/shared";
import {
  useEffect,
  useRef,
  useState,
  type ClipboardEvent,
  type FormEvent,
  type InputHTMLAttributes,
  type KeyboardEvent,
} from "react";
import { toast } from "sonner";
import { GithubMark } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { InfinityMark } from "@/components/ui/infinity-loader";
import { Field, Input } from "@/components/ui/primitives";
import { desktop } from "@/desktop/bridge";
import { API_BASE, errorMessage, post } from "@/lib/api";
import { keys, useConfig } from "@/lib/queries";
import type { Me } from "@/lib/types";
import { cn } from "@/lib/utils";

/** O "G" do Google (monocromático, como o resto da interface). */
function GoogleMark({ className = "h-4 w-4" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <path
        fill="currentColor"
        d="M21.8 10.2H12v3.9h5.6c-.5 2.5-2.7 4.3-5.6 4.3a6.4 6.4 0 1 1 4.2-11.2l2.8-2.8A10.3 10.3 0 1 0 12 22.3c5.9 0 10-4.1 10-10.1 0-.7-.1-1.4-.2-2Z"
      />
    </svg>
  );
}

export type AuthStep = "choose" | "email" | "register" | "verify" | "forgot" | "reset" | "dev" | "twofa";

/** Entrar com GitHub ou Google: no app, pelo navegador do sistema (RFC 8252); na web, redireciona. */
export function startOAuth(provider: "github" | "google", next: string) {
  if (desktop) {
    toast("Continue no navegador", { description: "Depois de autorizar, o Kompil volta sozinho." });
    void desktop.login(provider, next).then((r) => {
      if (!r.ok) toast.error(r.error ?? "Não foi possível entrar.");
    });
    return;
  }
  window.location.href = `${API_BASE}/auth/oauth/${provider}/start?redirect=${encodeURIComponent(next)}`;
}

/** Botão principal da entrada (branco): escurece de leve e sobe 1px ao passar o mouse. */
export const AUTH_PRIMARY =
  "h-11 w-full justify-center rounded-xl text-[13.5px] transition-[background-color,transform,box-shadow] duration-200 hover:-translate-y-px dark:hover:bg-[#e8e8e8] focus-visible:-translate-y-px active:translate-y-0";

/** Botões escuros da entrada: a borda clareia ao passar o mouse. */
export const AUTH_SECONDARY =
  "group relative flex h-11 w-full items-center justify-center gap-2.5 rounded-xl border border-border bg-bg text-[13.5px] font-medium text-fg transition-[border-color,background-color,transform] duration-200 hover:-translate-y-px hover:border-border-strong dark:border-[#3a3a3a] dark:hover:border-[#555] focus-visible:-translate-y-px focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60 active:translate-y-0 disabled:cursor-not-allowed disabled:opacity-45";

/** Continuar com GitHub / Google (o GitHub primeiro: é o que os devs mais usam). */
export function OAuthButtons({ next, verb = "Continuar" }: { next: string; verb?: string }) {
  const { data: config } = useConfig();
  const off = config && !config.auth.github && !config.auth.google;
  if (off) return null;
  return (
    <div className="space-y-2.5">
      <button
        type="button"
        className={AUTH_SECONDARY}
        onClick={() => startOAuth("github", next)}
        disabled={!config?.auth.github}
      >
        <GithubMark className="h-4 w-4" /> {verb} com GitHub
      </button>
      <button
        type="button"
        className={AUTH_SECONDARY}
        onClick={() => startOAuth("google", next)}
        disabled={!config?.auth.google}
      >
        <GoogleMark /> {verb} com Google
      </button>
    </div>
  );
}

/** "ou" entre as formas de entrar. */
function Divider({ label = "ou" }: { label?: string }) {
  return (
    <div
      role="separator"
      className="flex items-center gap-3 py-0.5 text-[11px] uppercase tracking-[0.18em] text-fg-subtle"
    >
      <span className="h-px flex-1 bg-gradient-to-r from-transparent to-border" />
      {label}
      <span className="h-px flex-1 bg-gradient-to-l from-transparent to-border" />
    </div>
  );
}

/** Título e texto de cada etapa (o diálogo do app e a página de login usam os mesmos). */
export const AUTH_STEPS: Record<AuthStep, { title: string; description: string }> = {
  choose: { title: "Entrar no Kompil", description: "Programe junto, ao vivo, com o seu time." },
  email: { title: "Que bom te ver de novo", description: "Entre com o e-mail e a senha da sua conta." },
  register: { title: "Criar a sua conta", description: "Grátis. Depois, três perguntas rápidas e pronto." },
  verify: { title: "Confirme o seu e-mail", description: "Enviamos um código de 6 números para você." },
  forgot: {
    title: "Esqueceu a senha?",
    description: "Digite o e-mail da conta: enviamos um código para trocar.",
  },
  reset: { title: "Criar uma senha nova", description: "Use o código que chegou no seu e-mail." },
  dev: {
    title: "Entrar com nome de dev",
    description: "Acesso rápido por @handle, só em servidores de desenvolvimento.",
  },
  twofa: {
    title: "Verificação em duas etapas",
    description: "Digite o código de 6 números do seu app autenticador.",
  },
};

type LoginResult = { me: Me } | TwoFactorChallenge | { verifyEmail: { email: string } };
type SecondFactorResult = { me: Me; redirectPath?: string } | { redirect: string };

function Back({ onClick, label = "Voltar" }: { onClick: () => void; label?: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="mb-4 inline-flex items-center gap-1.5 text-[12.5px] text-fg-muted transition-colors hover:text-fg"
    >
      <ArrowLeft className="h-3.5 w-3.5" /> {label}
    </button>
  );
}

/** Senha com o "olho" para mostrar/esconder. */
export function PasswordInput(props: Omit<InputHTMLAttributes<HTMLInputElement>, "type">) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="relative">
      <Input {...props} type={visible ? "text" : "password"} className={cn("h-10 pr-10", props.className)} />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        className="absolute right-1 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-md text-fg-subtle transition-colors hover:bg-bg-muted hover:text-fg"
        aria-label={visible ? "Esconder a senha" : "Mostrar a senha"}
        aria-pressed={visible}
        tabIndex={-1}
      >
        {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
}

/** Força da senha (só uma dica visual; o servidor exige 8+ caracteres). */
function strengthOf(p: string): 0 | 1 | 2 | 3 {
  if (p.length < 8) return 0;
  const kinds = [/[a-z]/, /[A-Z]/, /\d/, /[^A-Za-z0-9]/].filter((r) => r.test(p)).length;
  if (p.length >= 12 && kinds >= 3) return 3;
  if (kinds >= 2) return 2;
  return 1;
}

function Strength({ password }: { password: string }) {
  const s = strengthOf(password);
  const label = ["Pelo menos 8 caracteres", "Fraca", "Boa", "Forte"][s];
  return (
    <div className="mt-1.5 flex items-center gap-2">
      <div className="flex flex-1 gap-1" aria-hidden>
        {[1, 2, 3].map((i) => (
          <span
            key={i}
            className={cn(
              "h-1 flex-1 rounded-full transition-colors duration-300",
              s >= i ? "bg-fg" : "bg-border",
            )}
          />
        ))}
      </div>
      <span className="w-40 text-right text-[11px] text-fg-subtle">{label}</span>
    </div>
  );
}

/**
 * Código de 6 números em seis caixas: digita, apaga e volta, cola o código inteiro. Cada número
 * entra com uma animação; `status` pinta as caixas (erro treme, certo acende).
 */
export function CodeInput({
  value,
  onChange,
  onComplete,
  status = "idle",
  autoFocus,
  label,
}: {
  value: string;
  onChange: (v: string) => void;
  onComplete?: (v: string) => void;
  status?: "idle" | "error" | "ok" | "busy";
  autoFocus?: boolean;
  label: string;
}) {
  const refs = useRef<Array<HTMLInputElement | null>>([]);
  const digits = value.padEnd(6, " ").slice(0, 6).split("");
  useEffect(() => {
    if (autoFocus) refs.current[Math.min(value.length, 5)]?.focus();
    // Só ao montar: o foco depois segue o que a pessoa digita.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const set = (next: string) => {
    const clean = next.replace(/\D/g, "").slice(0, 6);
    onChange(clean);
    if (clean.length === 6) onComplete?.(clean);
    refs.current[Math.min(clean.length, 5)]?.focus();
  };
  const onKey = (i: number) => (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      set(value.slice(0, Math.max(0, value.length - (value.length > i ? value.length - i : 1))));
    } else if (e.key === "ArrowLeft") refs.current[Math.max(0, i - 1)]?.focus();
    else if (e.key === "ArrowRight") refs.current[Math.min(5, i + 1)]?.focus();
  };
  const onPaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    set(e.clipboardData.getData("text"));
  };
  return (
    <div
      role="group"
      aria-label={label}
      className={cn("flex justify-center gap-2", status === "error" && "animate-shake")}
    >
      {digits.map((d, i) => (
        <input
          key={i}
          ref={(el) => {
            refs.current[i] = el;
          }}
          value={d.trim()}
          inputMode="numeric"
          autoComplete={i === 0 ? "one-time-code" : "off"}
          aria-label={`${label}: número ${i + 1}`}
          disabled={status === "busy" || status === "ok"}
          onChange={(e) => {
            const typed = e.target.value.replace(/\D/g, "");
            if (!typed) return;
            set((value.slice(0, i) + typed).slice(0, 6));
          }}
          onKeyDown={onKey(i)}
          onPaste={onPaste}
          onFocus={(e) => e.target.select()}
          className={cn(
            "h-14 w-11 rounded-xl border bg-bg text-center font-mono text-[24px] font-semibold text-fg outline-none transition-[border-color,box-shadow,transform,background] duration-200 focus:border-fg focus:shadow-[0_0_0_4px_var(--accent-soft)] sm:w-12",
            d.trim() ? "animate-pop border-border-strong" : "border-border",
            status === "error" && "border-danger",
            status === "ok" && "border-fg bg-fg text-fg-inverse",
            i === 2 && "mr-2",
          )}
        />
      ))}
    </div>
  );
}

/** Confirmação curta antes de seguir (o símbolo fecha e aparece o "pronto"). */
function SuccessBurst({ label }: { label: string }) {
  return (
    <div className="flex animate-fade-up flex-col items-center gap-3 py-6 text-center" role="status">
      <div className="relative">
        <InfinityMark className="h-[50px] w-[100px] text-fg" label={null} track={false} />
        <span className="absolute -right-2 -top-2 flex h-6 w-6 animate-pop items-center justify-center rounded-full bg-fg text-fg-inverse">
          <Check className="h-3.5 w-3.5" strokeWidth={3} />
        </span>
      </div>
      <p className="text-[14px] font-medium text-fg">{label}</p>
    </div>
  );
}

/** Contagem para pedir outro código (o servidor aceita um por minuto). */
function useCountdown(seconds: number) {
  const [left, setLeft] = useState(seconds);
  useEffect(() => {
    if (left <= 0) return;
    const t = setTimeout(() => setLeft((v) => v - 1), 1000);
    return () => clearTimeout(t);
  }, [left]);
  return { left, restart: () => setLeft(seconds) };
}

/**
 * Entrar e criar conta: escolher a forma, e-mail e senha, cadastro com código no e-mail, esqueci a
 * senha (código + senha nova), verificação em duas etapas e (fora de produção) nome de dev. Usado
 * no diálogo "Entrar" do app e na página /login.
 */
export function AuthFlow({
  step,
  onStep,
  next = "/projects",
  onDone,
  ticket: initialTicket,
  backLabel,
  withOAuth = false,
}: {
  step: AuthStep;
  onStep: (step: AuthStep) => void;
  next?: string;
  /** Recebe a conta que acabou de entrar (para decidir para onde ir). */
  onDone?: (me: Me) => void;
  /** Texto do "voltar" das caixas de e-mail e de cadastro. */
  backLabel?: string;
  /** GitHub e Google também nas caixas de e-mail e de cadastro (tela de entrada). */
  withOAuth?: boolean;
  /** Bilhete da segunda etapa (retorno do GitHub/Google para quem tem duas etapas). */
  ticket?: string;
}) {
  const [ticket, setTicket] = useState(initialTicket ?? "");
  const [code, setCode] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  const { data: config } = useConfig();
  const qc = useQueryClient();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [handle, setHandle] = useState("");
  const [devName, setDevName] = useState("");
  const [welcome, setWelcome] = useState<string | null>(null);
  const hasOAuth = Boolean(config?.auth.github || config?.auth.google);
  const resend = useCountdown(60);

  const done = (me: Me, message = `Olá, ${me.user.name.split(" ")[0]}!`) => {
    // Um instante de "pronto" antes de trocar de tela.
    setWelcome(message);
    setTimeout(() => {
      qc.setQueryData(keys.me, me);
      onDone?.(me);
    }, 650);
  };
  const handle2fa = (r: LoginResult | { me: Me }) => {
    if ("twoFactor" in r) {
      setTicket(r.twoFactor.ticket);
      setCode("");
      onStep("twofa");
    } else if ("verifyEmail" in r) {
      setEmail(r.verifyEmail.email);
      setCode("");
      resend.restart();
      onStep("verify");
    } else done(r.me);
  };

  const login = useMutation({
    mutationFn: () => post<LoginResult>("/auth/login", { email, password }),
    onSuccess: handle2fa,
  });
  const register = useMutation({
    mutationFn: () => post<LoginResult>("/auth/register", { name, email, password }),
    onSuccess: handle2fa,
  });
  const verify = useMutation({
    mutationFn: (c: string) => post<LoginResult>("/auth/verify-email", { email, code: c }),
    onSuccess: (r) => {
      if ("me" in r) done(r.me, "E-mail confirmado!");
      else handle2fa(r);
    },
  });
  const resendVerify = useMutation({
    mutationFn: () => post("/auth/verify-email/resend", { email }),
    onSuccess: () => {
      // O código anterior deixou de valer: limpa o que foi digitado e o erro dele.
      setCode("");
      verify.reset();
      resend.restart();
      toast.success("Código novo enviado.");
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const forgot = useMutation({
    mutationFn: () => post("/auth/password/forgot", { email }),
    onSuccess: () => {
      setCode("");
      setPassword("");
      resend.restart();
      onStep("reset");
    },
  });
  const reset = useMutation({
    mutationFn: async () => {
      await post("/auth/password/reset", { email, code, password });
      return post<LoginResult>("/auth/login", { email, password });
    },
    onSuccess: (r) => {
      toast.success("Senha trocada. Todas as outras sessões foram encerradas.");
      handle2fa(r);
    },
  });
  const secondFactor = useMutation({
    mutationFn: (c?: string) => post<SecondFactorResult>("/auth/2fa", { ticket, code: c ?? code }),
    onSuccess: (r) => {
      // Login do app desktop/mobile pelo navegador: o código volta para o app.
      if ("redirect" in r) {
        window.location.href = r.redirect;
        return;
      }
      done(r.me, "Identidade confirmada");
      if (r.redirectPath && !onDone) setTimeout(() => window.location.assign(r.redirectPath!), 650);
    },
  });
  const devLogin = useMutation({
    mutationFn: () => post<{ me: Me }>("/auth/dev-login", { handle, name: devName || handle }),
    onSuccess: (r) => done(r.me),
  });

  const go = (s: AuthStep) => {
    for (const m of [login, register, verify, forgot, reset, secondFactor, devLogin]) m.reset();
    onStep(s);
  };
  const submit = (fn: () => void) => (e: FormEvent) => {
    e.preventDefault();
    fn();
  };

  if (welcome) return <SuccessBurst label={welcome} />;

  if (step === "email") {
    return (
      <div key="email" className="animate-fade-up">
        <Back onClick={() => go("choose")} label={backLabel} />
        <form onSubmit={submit(() => login.mutate())} className="space-y-3.5">
          <Field label="E-mail" htmlFor="auth-email">
            <Input
              id="auth-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="voce@exemplo.com"
              autoComplete="email"
              autoFocus
              required
              className="h-10"
            />
          </Field>
          <div className="space-y-1">
            <div className="flex items-baseline justify-between">
              <label htmlFor="auth-password" className="text-xs font-medium text-fg-muted">
                Senha
              </label>
              <button
                type="button"
                className="text-[12px] text-fg-muted underline-offset-4 transition-colors hover:text-fg hover:underline"
                onClick={() => go("forgot")}
              >
                Esqueceu a senha?
              </button>
            </div>
            <PasswordInput
              id="auth-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
            {login.isError ? (
              <p role="alert" className="animate-fade-up text-xs font-medium text-danger">
                {errorMessage(login.error)}
              </p>
            ) : null}
          </div>
          <Button
            type="submit"
            variant="primary"
            size="lg"
            className={AUTH_PRIMARY}
            loading={login.isPending}
          >
            Entrar <ArrowRight className="h-3.5 w-3.5" />
          </Button>
        </form>
        {withOAuth && hasOAuth ? (
          <div className="mt-4 space-y-3">
            <Divider />
            <OAuthButtons next={next} />
          </div>
        ) : null}
        <p className="mt-5 text-center text-[12.5px] text-fg-muted">
          Ainda não tem conta?{" "}
          <button
            type="button"
            className="font-medium text-fg underline underline-offset-4"
            onClick={() => go("register")}
          >
            Criar conta grátis
          </button>
        </p>
      </div>
    );
  }

  if (step === "register") {
    return (
      <div key="register" className="animate-fade-up">
        <Back onClick={() => go("choose")} label={backLabel} />
        {/* GitHub/Google primeiro: a conta nasce na hora, com nome, foto e e-mail já confirmado. */}
        {hasOAuth ? (
          <div className="mb-4 space-y-3">
            <OAuthButtons next={next} verb="Criar conta" />
            <Divider label="ou com e-mail" />
          </div>
        ) : null}
        <form onSubmit={submit(() => register.mutate())} className="space-y-3.5">
          <Field label="Nome" htmlFor="reg-name">
            <Input
              id="reg-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ana Souza"
              autoComplete="name"
              autoFocus
              required
              minLength={2}
              className="h-10"
            />
          </Field>
          <Field label="E-mail" htmlFor="reg-email" hint="Vamos mandar um código para confirmar.">
            <Input
              id="reg-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="voce@exemplo.com"
              autoComplete="email"
              required
              className="h-10"
            />
          </Field>
          <div className="space-y-1">
            <label htmlFor="reg-password" className="block text-xs font-medium text-fg-muted">
              Senha
            </label>
            <PasswordInput
              id="reg-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              required
              minLength={8}
            />
            <Strength password={password} />
            {register.isError ? (
              <p role="alert" className="animate-fade-up text-xs font-medium text-danger">
                {errorMessage(register.error)}
              </p>
            ) : null}
          </div>
          <Button
            type="submit"
            variant="primary"
            size="lg"
            className={AUTH_PRIMARY}
            loading={register.isPending}
          >
            Criar conta <ArrowRight className="h-3.5 w-3.5" />
          </Button>
        </form>
        <p className="mt-5 text-center text-[12.5px] text-fg-muted">
          Já tem conta?{" "}
          <button
            type="button"
            className="font-medium text-fg underline underline-offset-4"
            onClick={() => go("email")}
          >
            Entrar
          </button>
        </p>
      </div>
    );
  }

  if (step === "verify") {
    const status = verify.isPending ? "busy" : verify.isError ? "error" : "idle";
    return (
      <div key="verify" className="animate-fade-up text-center">
        <span className="mx-auto mb-4 flex h-12 w-12 animate-float items-center justify-center rounded-2xl border border-border bg-bg">
          <Mail className="h-5 w-5" />
        </span>
        <p className="mb-5 text-[13px] text-fg-muted">
          Digite o código enviado para <span className="font-medium text-fg">{email}</span>.
        </p>
        <CodeInput
          label="Código do e-mail"
          value={code}
          onChange={(v) => {
            setCode(v);
            if (verify.isError) verify.reset();
          }}
          onComplete={(v) => verify.mutate(v)}
          status={status}
          autoFocus
        />
        <div className="mt-3 min-h-5">
          {verify.isPending ? (
            <InfinityMark className="mx-auto h-4 w-8 text-fg-muted" track={false} />
          ) : verify.isError ? (
            <p role="alert" className="text-[12.5px] font-medium text-danger">
              {errorMessage(verify.error)}
            </p>
          ) : null}
        </div>
        <p className="mt-4 text-[12.5px] text-fg-muted">
          Não chegou? Confira o spam ou{" "}
          <button
            type="button"
            disabled={resend.left > 0 || resendVerify.isPending}
            onClick={() => resendVerify.mutate()}
            className="font-medium text-fg underline underline-offset-4 disabled:cursor-default disabled:no-underline disabled:opacity-60"
          >
            {resend.left > 0 ? `peça outro em ${resend.left}s` : "peça outro código"}
          </button>
        </p>
        <button
          type="button"
          onClick={() => go("register")}
          className="mt-3 text-[12px] text-fg-subtle hover:text-fg"
        >
          Usar outro e-mail
        </button>
      </div>
    );
  }

  if (step === "forgot") {
    return (
      <div key="forgot" className="animate-fade-up">
        <Back onClick={() => go("email")} label="Voltar para entrar" />
        <form onSubmit={submit(() => forgot.mutate())} className="space-y-3.5">
          <Field
            label="E-mail da conta"
            htmlFor="forgot-email"
            error={forgot.isError ? errorMessage(forgot.error) : null}
          >
            <Input
              id="forgot-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="voce@exemplo.com"
              autoComplete="email"
              autoFocus
              required
              className="h-10"
            />
          </Field>
          <Button
            type="submit"
            variant="primary"
            size="lg"
            className={AUTH_PRIMARY}
            loading={forgot.isPending}
          >
            Enviar código <ArrowRight className="h-3.5 w-3.5" />
          </Button>
        </form>
      </div>
    );
  }

  if (step === "reset") {
    return (
      <div key="reset" className="animate-fade-up">
        <Back onClick={() => go("forgot")} label="Usar outro e-mail" />
        <p className="mb-4 text-[13px] text-fg-muted">
          Se existir uma conta com <span className="font-medium text-fg">{email}</span>, o código chegou lá.
        </p>
        <form onSubmit={submit(() => reset.mutate())} className="space-y-4">
          <CodeInput
            label="Código do e-mail"
            value={code}
            onChange={setCode}
            status={reset.isError ? "error" : "idle"}
            autoFocus
          />
          <div className="space-y-1">
            <label htmlFor="reset-password" className="block text-xs font-medium text-fg-muted">
              Senha nova
            </label>
            <PasswordInput
              id="reset-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              required
              minLength={8}
            />
            <Strength password={password} />
          </div>
          {reset.isError ? (
            <p role="alert" className="animate-fade-up text-xs font-medium text-danger">
              {errorMessage(reset.error)}
            </p>
          ) : null}
          <Button
            type="submit"
            variant="primary"
            size="lg"
            className={AUTH_PRIMARY}
            loading={reset.isPending}
            disabled={code.length !== 6 || password.length < 8}
          >
            Trocar a senha e entrar <ArrowRight className="h-3.5 w-3.5" />
          </Button>
        </form>
        <p className="mt-4 text-center text-[12.5px] text-fg-muted">
          <button
            type="button"
            disabled={resend.left > 0 || forgot.isPending}
            onClick={() =>
              // Na etapa do código o erro do pedido não tem campo para aparecer: vai em aviso.
              forgot.mutate(undefined, {
                onSuccess: () => {
                  reset.reset();
                  toast.success("Código novo enviado.");
                },
                onError: (e) => toast.error(errorMessage(e)),
              })
            }
            className="font-medium text-fg underline underline-offset-4 disabled:cursor-default disabled:no-underline disabled:opacity-60"
          >
            {resend.left > 0 ? `Pedir outro código em ${resend.left}s` : "Pedir outro código"}
          </button>
        </p>
      </div>
    );
  }

  if (step === "twofa") {
    const failed = secondFactor.isError;
    const expired = failed && /expirou|tentativas/i.test(errorMessage(secondFactor.error));
    return (
      <div key="twofa" className="animate-fade-up text-center">
        <div className="relative mx-auto mb-5 flex h-16 w-16 items-center justify-center">
          <span className="absolute inset-0 animate-ping-slow rounded-2xl border border-fg/20" aria-hidden />
          <span className="relative flex h-14 w-14 items-center justify-center rounded-2xl border border-border-strong bg-bg shadow-[0_12px_40px_-12px_var(--glow)]">
            <ShieldCheck className="h-6 w-6" />
          </span>
        </div>
        <p className="mb-5 text-[13px] leading-relaxed text-fg-muted">
          {useRecovery
            ? "Digite um dos códigos de recuperação que você guardou ao ligar as duas etapas."
            : "Abra o app autenticador (Google Authenticator, Microsoft Authenticator, 1Password…) e digite o código do Kompil."}
        </p>
        {useRecovery ? (
          <form onSubmit={submit(() => secondFactor.mutate(undefined))} className="space-y-3 text-left">
            <Input
              aria-label="Código de recuperação"
              value={code}
              onChange={(e) => setCode(e.target.value.slice(0, 12))}
              placeholder="abcd-ef23"
              autoFocus
              required
              className="h-11 text-center font-mono text-[16px] tracking-[0.2em]"
            />
            <Button
              type="submit"
              variant="primary"
              size="lg"
              className={AUTH_PRIMARY}
              loading={secondFactor.isPending}
              disabled={!ticket || code.trim().length < 8 || expired}
            >
              Verificar <ArrowRight className="h-3.5 w-3.5" />
            </Button>
          </form>
        ) : (
          <CodeInput
            label="Código do app autenticador"
            value={code}
            onChange={(v) => {
              setCode(v);
              if (secondFactor.isError) secondFactor.reset();
            }}
            onComplete={(v) => {
              if (ticket) secondFactor.mutate(v);
            }}
            status={secondFactor.isPending ? "busy" : failed ? "error" : "idle"}
            autoFocus
          />
        )}
        <div className="mt-3 min-h-5">
          {secondFactor.isPending && !useRecovery ? (
            <InfinityMark className="mx-auto h-4 w-8 text-fg-muted" track={false} />
          ) : failed ? (
            <p role="alert" className="text-[12.5px] font-medium text-danger">
              {errorMessage(secondFactor.error)}
            </p>
          ) : null}
        </div>
        <button
          type="button"
          onClick={() => {
            setUseRecovery((v) => !v);
            setCode("");
            secondFactor.reset();
          }}
          className="mt-4 inline-flex items-center justify-center gap-1.5 text-[12px] text-fg-subtle hover:text-fg"
        >
          <KeyRound className="h-3.5 w-3.5" />
          {useRecovery ? "Usar o código do app autenticador" : "Sem o celular? Use um código de recuperação"}
        </button>
        {expired ? (
          <Button variant="secondary" className="mt-3 w-full justify-center" onClick={() => go("choose")}>
            Entrar de novo
          </Button>
        ) : null}
      </div>
    );
  }

  if (step === "dev") {
    return (
      <div key="dev" className="animate-fade-up">
        <Back onClick={() => go("choose")} />
        <form onSubmit={submit(() => devLogin.mutate())} className="space-y-3">
          <Field
            label="@handle"
            htmlFor="dev-handle"
            hint="Cria a conta na hora se ainda não existir."
            error={devLogin.isError ? errorMessage(devLogin.error) : null}
          >
            <Input
              id="dev-handle"
              value={handle}
              onChange={(e) => setHandle(e.target.value.toLowerCase())}
              placeholder="ana"
              autoComplete="username"
              autoFocus
              required
              minLength={3}
            />
          </Field>
          <Field label="Nome (opcional)" htmlFor="dev-name">
            <Input
              id="dev-name"
              value={devName}
              onChange={(e) => setDevName(e.target.value)}
              placeholder="Ana Souza"
            />
          </Field>
          <Button
            type="submit"
            variant="primary"
            size="lg"
            className="w-full justify-center"
            loading={devLogin.isPending}
          >
            Entrar como @{handle || "…"} <ArrowRight className="h-3.5 w-3.5" />
          </Button>
        </form>
        <p className="mt-4 flex items-center justify-center gap-1.5 text-[11.5px] text-fg-subtle">
          <Terminal className="h-3 w-3" /> Desligado automaticamente em produção.
        </p>
      </div>
    );
  }

  return (
    <div key="choose" className="space-y-3.5">
      {hasOAuth ? (
        <>
          <OAuthButtons next={next} />
          <Divider />
        </>
      ) : null}
      <Button variant="primary" size="lg" className={AUTH_PRIMARY} onClick={() => go("email")}>
        <Mail className="h-4 w-4" /> Entrar com e-mail
      </Button>
      <p className="pt-1 text-center text-[13px] text-fg-muted">
        Não tem conta?{" "}
        <button
          type="button"
          className="font-medium text-fg underline-offset-4 transition-colors hover:underline"
          onClick={() => go("register")}
        >
          Criar grátis
        </button>
      </p>
      {config?.auth.dev ? (
        <button
          type="button"
          onClick={() => go("dev")}
          className="flex w-full items-center justify-center gap-1.5 pt-1 text-[12px] text-fg-subtle hover:text-fg"
        >
          <AtSign className="h-3.5 w-3.5" /> Entrar com nome de dev (desenvolvimento)
        </button>
      ) : null}
    </div>
  );
}
